import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { CheckCircle, ArrowRight, ArrowLeft } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { HeroImage } from '@/components/HeroImage';

const step1Schema = z.object({
  fullName: z.string().trim().min(2, 'Name must be at least 2 characters').max(100),
  email: z.string().trim().email('Invalid email address').max(255),
  phone: z.string().trim().min(10, 'Phone must be at least 10 digits').max(20),
  street: z.string().trim().min(5, 'Street address is required').max(200),
  city: z.string().trim().min(2, 'City is required').max(100),
  state: z.string().min(2, 'State is required'),
  zip: z.string().trim().min(5, 'ZIP code must be at least 5 digits').max(10),
  ssnLast4: z.string().trim().length(4, 'Must be exactly 4 digits').regex(/^\d+$/, 'Must be numbers only'),
  password: z.string().min(8, 'Password must be at least 8 characters').max(100),
  confirmPassword: z.string(),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

const step2Schema = z.object({
  filingStatus: z.string().min(1, 'Filing status is required'),
  numDependents: z.string(),
  previousYearAgi: z.string(),
});

const step3Schema = z.object({
  contactMethod: z.string().min(1, 'Contact method is required'),
  notes: z.string().max(1000).optional(),
});

type Step1Data = z.infer<typeof step1Schema>;
type Step2Data = z.infer<typeof step2Schema>;
type Step3Data = z.infer<typeof step3Schema>;

export default function NewClient() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { signUp } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<Partial<Step1Data & Step2Data & Step3Data>>({});

  // Step 1 form
  const step1Form = useForm<Step1Data>({
    resolver: zodResolver(step1Schema),
    defaultValues: formData as Step1Data,
  });

  // Step 2 form
  const step2Form = useForm<Step2Data>({
    resolver: zodResolver(step2Schema),
    defaultValues: formData as Step2Data,
  });

  // Step 3 form
  const step3Form = useForm<Step3Data>({
    resolver: zodResolver(step3Schema),
    defaultValues: formData as Step3Data,
  });

  const steps = [
    { number: 1, title: 'Personal Information', description: 'Basic contact details' },
    { number: 2, title: 'Filing Information', description: 'Tax filing details' },
    { number: 3, title: 'Preferences', description: 'Communication preferences' },
  ];

  const handleStep1Submit = (data: Step1Data) => {
    setFormData((prev) => ({ ...prev, ...data }));
    setCurrentStep(2);
  };

  const handleStep2Submit = (data: Step2Data) => {
    setFormData((prev) => ({ ...prev, ...data }));
    setCurrentStep(3);
  };

  const handleFinalSubmit = async (data: Step3Data) => {
    const allData = { ...formData, ...data };
    setIsSubmitting(true);

    try {
      // Create auth account first
      const { error: signUpError } = await signUp(
        allData.email!,
        allData.password!,
        allData.fullName!
      );

      if (signUpError) {
        // Handle case where email already exists
        if (signUpError.message?.includes('already registered') || signUpError.message?.includes('User already')) {
          toast({
            title: 'Email already registered',
            description: 'An account with this email already exists. Please log in instead.',
            variant: 'destructive',
          });
          setTimeout(() => navigate('/auth/login'), 2000);
          return;
        }
        throw signUpError;
      }

      // Get the newly created user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError || !user) {
        throw new Error('Failed to get user after signup');
      }

      // Check if client record exists by email (from appointment booking)
      let { data: clientData, error: clientCheckError } = await supabase
        .from('clients')
        .select('id')
        .eq('email', allData.email!.toLowerCase().trim())
        .maybeSingle();

      if (clientCheckError) throw clientCheckError;

      let clientId: string;

      if (!clientData) {
        // No existing client found - create new record
        const { data: newClient, error: createError } = await supabase
          .from('clients')
          .insert({
            user_id: user.id,
            full_name: allData.fullName!,
            email: allData.email!,
            phone: allData.phone!,
            address: {
              street: allData.street!,
              city: allData.city!,
              state: allData.state!,
              zip: allData.zip!,
            },
            ssn_last_4: allData.ssnLast4!,
            filing_status: allData.filingStatus!,
            previous_year_agi: allData.previousYearAgi ? parseFloat(allData.previousYearAgi) : null,
            preferred_contact_method: allData.contactMethod!,
            client_status: 'active',
          })
          .select()
          .single();

        if (createError) throw createError;
        clientId = newClient.id;
      } else {
        // Client exists (likely from appointment booking) - link to new user account
        clientId = clientData.id;
        const { error: updateError } = await supabase
          .from('clients')
          .update({
            user_id: user.id, // Link to the new user account
            full_name: allData.fullName!,
            phone: allData.phone!,
            address: {
              street: allData.street!,
              city: allData.city!,
              state: allData.state!,
              zip: allData.zip!,
            },
            ssn_last_4: allData.ssnLast4!,
            filing_status: allData.filingStatus!,
            previous_year_agi: allData.previousYearAgi ? parseFloat(allData.previousYearAgi) : null,
            preferred_contact_method: allData.contactMethod!,
          })
          .eq('id', clientId);
        
        if (updateError) throw updateError;
      }

      // Create intake form record
      await supabase
        .from('intake_forms')
        .insert({
          client_id: clientId,
          form_data: allData,
          status: 'completed',
          completed_at: new Date().toISOString(),
        });

      toast({
        title: 'Welcome! Account created successfully',
        description: 'Your intake form has been submitted. You can now upload your tax documents.',
      });

      // Redirect to document upload
      setTimeout(() => {
        navigate('/client-portal/documents');
      }, 2000);
    } catch (error: any) {
      console.error('Error submitting intake form:', error);
      toast({
        title: 'Submission failed',
        description: error.message || 'There was an error submitting your form. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const progressPercentage = (currentStep / steps.length) * 100;

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary via-secondary to-accent text-primary-foreground py-12 md:py-16 overflow-hidden">
        <div className="absolute inset-0">
          <HeroImage
            src="/images/hero-new-client.png"
            alt="Clover - Ready to help with your intake form"
            priority={true}
            width={1920}
            height={600}
          />
          <div className="absolute inset-0 bg-gradient-to-br from-primary/40 via-secondary/40 to-accent/40" />
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center space-y-4">
            <h1 className="text-4xl md:text-5xl font-bold drop-shadow-lg">
              New Client Intake Form
            </h1>
            <p className="text-xl text-primary-foreground/90">
              Let's get started! Complete this form to begin your tax preparation journey.
            </p>
          </div>
        </div>
      </section>

      {/* Progress Steps */}
      <section className="py-8 bg-muted/30 border-b">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="mb-6">
              <Progress value={progressPercentage} className="h-2" />
              <p className="text-center text-sm text-muted-foreground mt-2">
                Step {currentStep} of {steps.length}
              </p>
            </div>
            <div className="grid grid-cols-3 gap-4">
              {steps.map((step) => (
                <div
                  key={step.number}
                  className={`text-center ${
                    step.number === currentStep ? 'opacity-100' : 'opacity-50'
                  }`}
                >
                  <div
                    className={`inline-flex items-center justify-center w-10 h-10 rounded-full mb-2 ${
                      step.number < currentStep
                        ? 'bg-primary text-primary-foreground'
                        : step.number === currentStep
                        ? 'bg-secondary text-secondary-foreground'
                        : 'bg-muted text-muted-foreground'
                    }`}
                  >
                    {step.number < currentStep ? (
                      <CheckCircle className="h-5 w-5" />
                    ) : (
                      <span>{step.number}</span>
                    )}
                  </div>
                  <h3 className="font-semibold text-sm">{step.title}</h3>
                  <p className="text-xs text-muted-foreground">{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Form Content */}
      <section className="py-12 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto">
            {/* Step 1: Personal Information */}
            {currentStep === 1 && (
              <Card>
                <CardHeader>
                  <CardTitle>Personal Information</CardTitle>
                  <CardDescription>
                    Please provide your basic contact information
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={step1Form.handleSubmit(handleStep1Submit)} className="space-y-4">
                    <div>
                      <Label htmlFor="fullName">Full Name *</Label>
                      <Input
                        id="fullName"
                        {...step1Form.register('fullName')}
                        placeholder="John Smith"
                      />
                      {step1Form.formState.errors.fullName && (
                        <p className="text-sm text-destructive mt-1">
                          {step1Form.formState.errors.fullName.message}
                        </p>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="email">Email *</Label>
                        <Input
                          id="email"
                          type="email"
                          {...step1Form.register('email')}
                          placeholder="john@example.com"
                        />
                        {step1Form.formState.errors.email && (
                          <p className="text-sm text-destructive mt-1">
                            {step1Form.formState.errors.email.message}
                          </p>
                        )}
                      </div>

                      <div>
                        <Label htmlFor="phone">Phone *</Label>
                        <Input
                          id="phone"
                          type="tel"
                          {...step1Form.register('phone')}
                          placeholder="(913) 555-0123"
                        />
                        {step1Form.formState.errors.phone && (
                          <p className="text-sm text-destructive mt-1">
                            {step1Form.formState.errors.phone.message}
                          </p>
                        )}
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="street">Street Address *</Label>
                      <Input
                        id="street"
                        {...step1Form.register('street')}
                        placeholder="123 Main St"
                      />
                      {step1Form.formState.errors.street && (
                        <p className="text-sm text-destructive mt-1">
                          {step1Form.formState.errors.street.message}
                        </p>
                      )}
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      <div className="col-span-2 md:col-span-1">
                        <Label htmlFor="city">City *</Label>
                        <Input
                          id="city"
                          {...step1Form.register('city')}
                          placeholder="Lenexa"
                        />
                        {step1Form.formState.errors.city && (
                          <p className="text-sm text-destructive mt-1">
                            {step1Form.formState.errors.city.message}
                          </p>
                        )}
                      </div>

                      <div>
                        <Label htmlFor="state">State *</Label>
                        <Input
                          id="state"
                          {...step1Form.register('state')}
                          placeholder="KS"
                          maxLength={2}
                        />
                        {step1Form.formState.errors.state && (
                          <p className="text-sm text-destructive mt-1">
                            {step1Form.formState.errors.state.message}
                          </p>
                        )}
                      </div>

                      <div>
                        <Label htmlFor="zip">ZIP Code *</Label>
                        <Input
                          id="zip"
                          {...step1Form.register('zip')}
                          placeholder="66219"
                        />
                        {step1Form.formState.errors.zip && (
                          <p className="text-sm text-destructive mt-1">
                            {step1Form.formState.errors.zip.message}
                          </p>
                        )}
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="ssnLast4">Last 4 Digits of SSN *</Label>
                      <Input
                        id="ssnLast4"
                        {...step1Form.register('ssnLast4')}
                        placeholder="1234"
                        maxLength={4}
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        For identification purposes only
                      </p>
                      {step1Form.formState.errors.ssnLast4 && (
                        <p className="text-sm text-destructive mt-1">
                          {step1Form.formState.errors.ssnLast4.message}
                        </p>
                      )}
                    </div>

                    <div className="border-t pt-4 mt-4">
                      <h3 className="text-sm font-semibold mb-3">Create Your Account</h3>
                      <div className="space-y-4">
                        <div>
                          <Label htmlFor="password">Password *</Label>
                          <Input
                            id="password"
                            type="password"
                            {...step1Form.register('password')}
                            placeholder="At least 8 characters"
                          />
                          {step1Form.formState.errors.password && (
                            <p className="text-sm text-destructive mt-1">
                              {step1Form.formState.errors.password.message}
                            </p>
                          )}
                        </div>

                        <div>
                          <Label htmlFor="confirmPassword">Confirm Password *</Label>
                          <Input
                            id="confirmPassword"
                            type="password"
                            {...step1Form.register('confirmPassword')}
                            placeholder="Re-enter your password"
                          />
                          {step1Form.formState.errors.confirmPassword && (
                            <p className="text-sm text-destructive mt-1">
                              {step1Form.formState.errors.confirmPassword.message}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="flex justify-end">
                      <Button type="submit">
                        Next Step
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </div>
                  </form>
                </CardContent>
              </Card>
            )}

            {/* Step 2: Filing Information */}
            {currentStep === 2 && (
              <Card>
                <CardHeader>
                  <CardTitle>Filing Information</CardTitle>
                  <CardDescription>
                    Tell us about your tax filing situation
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={step2Form.handleSubmit(handleStep2Submit)} className="space-y-4">
                    <div>
                      <Label htmlFor="filingStatus">Filing Status *</Label>
                      <Select
                        onValueChange={(value) => step2Form.setValue('filingStatus', value)}
                        defaultValue={step2Form.getValues('filingStatus')}
                      >
                        <SelectTrigger id="filingStatus">
                          <SelectValue placeholder="Select filing status" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="single">Single</SelectItem>
                          <SelectItem value="married-filing-jointly">Married Filing Jointly</SelectItem>
                          <SelectItem value="married-filing-separately">Married Filing Separately</SelectItem>
                          <SelectItem value="head-of-household">Head of Household</SelectItem>
                          <SelectItem value="qualifying-widow">Qualifying Widow(er)</SelectItem>
                        </SelectContent>
                      </Select>
                      {step2Form.formState.errors.filingStatus && (
                        <p className="text-sm text-destructive mt-1">
                          {step2Form.formState.errors.filingStatus.message}
                        </p>
                      )}
                    </div>

                    <div>
                      <Label htmlFor="numDependents">Number of Dependents (Optional)</Label>
                      <Input
                        id="numDependents"
                        type="number"
                        {...step2Form.register('numDependents')}
                        placeholder="0"
                        min="0"
                      />
                    </div>

                    <div>
                      <Label htmlFor="previousYearAgi">Previous Year AGI (Optional)</Label>
                      <Input
                        id="previousYearAgi"
                        type="number"
                        {...step2Form.register('previousYearAgi')}
                        placeholder="45000"
                        step="0.01"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Your Adjusted Gross Income from last year's tax return
                      </p>
                    </div>

                    <div className="flex justify-between">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setCurrentStep(1)}
                      >
                        <ArrowLeft className="mr-2 h-4 w-4" />
                        Back
                      </Button>
                      <Button type="submit">
                        Next Step
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </div>
                  </form>
                </CardContent>
              </Card>
            )}

            {/* Step 3: Preferences */}
            {currentStep === 3 && (
              <Card>
                <CardHeader>
                  <CardTitle>Communication Preferences</CardTitle>
                  <CardDescription>
                    How would you like us to contact you?
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={step3Form.handleSubmit(handleFinalSubmit)} className="space-y-4">
                    <div>
                      <Label htmlFor="contactMethod">Preferred Contact Method *</Label>
                      <Select
                        onValueChange={(value) => step3Form.setValue('contactMethod', value)}
                        defaultValue={step3Form.getValues('contactMethod')}
                      >
                        <SelectTrigger id="contactMethod">
                          <SelectValue placeholder="Select contact method" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="email">Email</SelectItem>
                          <SelectItem value="phone">Phone</SelectItem>
                          <SelectItem value="text">Text Message</SelectItem>
                        </SelectContent>
                      </Select>
                      {step3Form.formState.errors.contactMethod && (
                        <p className="text-sm text-destructive mt-1">
                          {step3Form.formState.errors.contactMethod.message}
                        </p>
                      )}
                    </div>

                    <div>
                      <Label htmlFor="notes">Additional Notes (Optional)</Label>
                      <Textarea
                        id="notes"
                        {...step3Form.register('notes')}
                        placeholder="Any special requests or information we should know..."
                        rows={4}
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Maximum 1000 characters
                      </p>
                    </div>

                    <div className="flex justify-between">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setCurrentStep(2)}
                        disabled={isSubmitting}
                      >
                        <ArrowLeft className="mr-2 h-4 w-4" />
                        Back
                      </Button>
                      <Button type="submit" disabled={isSubmitting}>
                        {isSubmitting ? 'Submitting...' : 'Complete Intake Form'}
                      </Button>
                    </div>
                  </form>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </section>
    </Layout>
  );
}
